/**
 * Active-client store for the Agents product (D2) — reactive (AMB-327).
 *
 * The Agents screens are separate route components with no shared parent provider, so
 * the active client lives in this tiny localStorage-backed external store rather than
 * React context. It exposes two read styles:
 *
 *  - **Synchronous** (`getActiveClient` / `getActiveClientSlug`) — for module-scope reads
 *    and non-render call sites. This is the back-compat SHIM: every screen that still does
 *    `const SLUG = getActiveClient().slug` keeps compiling and returns valid data.
 *  - **Reactive** (`useActiveClient` / `useActiveClientSlug` for screens,
 *    `useActiveClientChrome` for shared chrome) — `useSyncExternalStore` subscriptions, so
 *    switching clients re-renders in place with NO full-page reload.
 *
 * Migration is incremental: each screen swaps its module-scope read for the reactive hook
 * in its own ticket. Until a screen adopts the hook it can't react, so `setActiveClient`
 * falls back to a reload when no reactive screen is mounted — preserving today's behavior
 * for un-migrated screens. Once ≥1 reactive screen is on the page, the switch is reload-free.
 */

import { useEffect, useSyncExternalStore } from "react";

export interface ActiveClient {
  slug: string; // operator-API client slug — the screens' source of truth
  name: string;
  initials: string;
  color: string;
  primaryColor?: string; // brand color (hex); when set the avatar uses it instead of `color`
}

const KEY = "agents.activeClient";

// Default on first-ever login (E1 / AMB-349): the agency-wide "All Clients" view, so a
// new user isn't dropped into one (often irrelevant) client. slug "all" → the operator
// API aggregates every client (index.js). A manual switch persists across sessions via
// localStorage. Matches the ClientSwitcher's "All Clients" option exactly.
const DEFAULT: ActiveClient = {
  slug: "all",
  name: "All Clients",
  initials: "AG",
  color: "bg-gray-700",
};

function readStored(): ActiveClient {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const c = JSON.parse(raw);
      if (c && c.slug) return { ...DEFAULT, ...c };
    }
  } catch {}
  return DEFAULT;
}

// Single in-memory source of truth. Replaced with a NEW object only on a real change, so
// getActiveClient() stays referentially stable between renders (useSyncExternalStore needs
// a cached snapshot).
let current: ActiveClient = readStored();
const listeners = new Set<() => void>();
let reactiveScreens = 0; // mounted screens reading reactively — suppresses the reload shim

export function subscribeActiveClient(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Synchronous read (the back-compat shim). Stable reference until a real change. */
export function getActiveClient(): ActiveClient {
  return current;
}
export const getActiveClientSlug = (): string => current.slug;

/**
 * Reactive read for SCREENS. Mounting registers the screen as reactive so a client switch
 * re-renders it in place instead of reloading. Screens replace their module-scope
 * `const SLUG = getActiveClient().slug` with this hook in their own tickets.
 */
export function useActiveClient(): ActiveClient {
  useEffect(() => {
    reactiveScreens++;
    return () => {
      reactiveScreens--;
    };
  }, []);
  return useSyncExternalStore(subscribeActiveClient, getActiveClient, getActiveClient);
}
export const useActiveClientSlug = (): string => useActiveClient().slug;

/**
 * Reactive read for shared CHROME (ClientSwitcher, nav). Re-renders on switch but does NOT
 * count as a reactive screen, so it never masks an un-migrated screen's need to reload.
 */
export function useActiveClientChrome(): ActiveClient {
  return useSyncExternalStore(subscribeActiveClient, getActiveClient, getActiveClient);
}

// Current signed-in member's id (for submittedBy / attribution / defaults). The canonical
// roster + resolver live in roster.ts (AMB-358); this re-export keeps existing call sites
// importing from activeClient. NOTE: now returns `string | null` — an unrecognized login is
// null, NOT "kyle". Callers must treat null as the neutral "Everyone" view (D-1).
export { getCurrentMemberId } from "./roster";

/** Test-only: reset the in-memory store (the module holds state across a test file). */
export function __resetActiveClientForTests(c: ActiveClient = DEFAULT): void {
  current = { ...c };
  listeners.clear();
  reactiveScreens = 0;
}

/** Persist the selection and notify subscribers. Reloads only as a shim when no reactive
 *  screen is mounted (so un-migrated, module-scope screens still refresh). */
export function setActiveClient(client: ActiveClient): void {
  if (client.slug === current.slug) return; // no-op on same client
  current = { ...DEFAULT, ...client };
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {}
  listeners.forEach((l) => l()); // reactive screens + chrome re-render instantly
  if (reactiveScreens === 0) {
    try {
      window.location.reload();
    } catch {}
  }
}
